import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../../api/client';

interface KB {
  kb_id: string;
  title: string;
  content_type: string;
  source_url?: string;
  created_at: string;
}

export const AgentKnowledgeBase: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [items, setItems] = useState<KB[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');

  const load = async () => {
    const res = await api.get(`/api/v1/knowledge-base/${agentId}`);
    setItems(res.data.knowledge_base);
  };

  const search = async () => {
    if (!query) return load();
    const res = await api.get(`/api/v1/knowledge-base/${agentId}/search`, { params: { query, limit: 10 } });
    setItems(res.data.results);
  };

  const onDelete = async (kb_id: string) => {
    await api.delete(`/api/v1/knowledge-base/${kb_id}`);
    await load();
  };

  const onUpload = async () => {
    if (!file || !title) return;
    const form = new FormData();
    form.append('file', file);
    form.append('agent_id', agentId!);
    form.append('title', title);
    await api.post(`/api/v1/knowledge-base/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    setOpen(false);
    setFile(null);
    setTitle('');
    await load();
  };

  useEffect(() => { load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Knowledge Base</Typography>
        <Stack direction="row" spacing={1}>
          <TextField size="small" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} />
          <Button variant="outlined" onClick={search}>Search</Button>
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => setOpen(true)}>Upload</Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((kb) => (
              <TableRow key={kb.kb_id} hover>
                <TableCell>{kb.title}</TableCell>
                <TableCell>{kb.content_type}</TableCell>
                <TableCell>{kb.source_url || '-'}</TableCell>
                <TableCell>{new Date(kb.created_at).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => onDelete(kb.kb_id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload document</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <input type="file" accept=".pdf,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onUpload} disabled={!file || !title}>Upload</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};